// src/components/StatusBlock.jsx
//
// The claim/unclaim/set-status UI for one status/assignee/timestamp trio.
// Every stage table (Permit, Fieldwork, Site_Data, Final_Report,
// Digital_data, Artifact_submission) shares this exact shape by design
// (PROJECT_TRACKER_PLAN.md), and Site_Data/Final_Report each have a second,
// independent trio for revisions -- so StageCard renders this component
// once or twice rather than each stage page hand-rolling its own claim
// buttons.
//
// This component doesn't know about URLs or fetch -- it just calls the
// onClaim/onUnclaim/onSetStatus callbacks its parent gives it and shows a
// small error if one rejects. That keeps all the endpoint-path knowledge in
// StageCard, where the basePath/action-path decisions already live.

import { useState } from "react";
import { statusLabel } from "../constants";

export default function StatusBlock({
  label,
  status,
  assignedToUsername,
  statusChangedAt,
  statusChoices,
  currentUsername,
  isAdmin,
  onClaim,
  onUnclaim,
  onSetStatus,
  compact = false,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isUnclaimed = !assignedToUsername;
  const isMine = assignedToUsername === currentUsername;
  const canManage = isMine || isAdmin;

  const run = async (fn) => {
    setError("");
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(err.message || "That didn't work.");
    } finally {
      setBusy(false);
    }
  };

  if (compact) {
    // Used in the project list table -- just enough to claim/unclaim at a
    // glance. Status changes and the assignee/timestamp detail stay on the
    // project detail page, which uses the full (non-compact) block below.
    return (
      <div className="status-block status-block-compact">
        <div className="status-block-compact-row">
          <span
            className={`status-badge status-${status || "none"}`}
            title={assignedToUsername ? `Claimed by ${assignedToUsername}` : "Unclaimed"}
          >
            {status ? statusLabel(statusChoices, status) : "--"}
          </span>
          {isUnclaimed && (
            <button type="button" disabled={busy} onClick={() => run(onClaim)}>
              Claim
            </button>
          )}
          {!isUnclaimed && canManage && (
            <button type="button" disabled={busy} onClick={() => run(onUnclaim)}>
              Unclaim
            </button>
          )}
        </div>
        {error && <p className="form-error form-error-compact">{error}</p>}
      </div>
    );
  }

  return (
    <div className="status-block">
      <div className="status-block-row">
        {label && <span className="status-block-label">{label}</span>}
        <span className={`status-badge status-${status || "none"}`}>
          {status ? statusLabel(statusChoices, status) : "--"}
        </span>
        <span className="status-block-assignee">
          {assignedToUsername ? `Claimed by ${assignedToUsername}` : "Unclaimed"}
        </span>
      </div>

      <div className="status-block-actions">
        {isUnclaimed && (
          <button type="button" disabled={busy} onClick={() => run(onClaim)}>
            Claim
          </button>
        )}
        {!isUnclaimed && canManage && (
          <button type="button" disabled={busy} onClick={() => run(onUnclaim)}>
            Unclaim
          </button>
        )}
        {!isUnclaimed && canManage && (
          <select
            disabled={busy}
            value={status || ""}
            onChange={(e) => run(() => onSetStatus(e.target.value))}
          >
            {statusChoices.map(([value, choiceLabel]) => (
              <option key={value} value={value}>
                {choiceLabel}
              </option>
            ))}
          </select>
        )}
      </div>

      {statusChangedAt && (
        <p className="status-block-timestamp">
          Updated {new Date(statusChangedAt).toLocaleString()}
        </p>
      )}
      {error && <p className="form-error">{error}</p>}
    </div>
  );
}
